import { dashboardPathForRole } from './roleDashboard';

export const PATIENT_DASHBOARD_PATH = '/patient-dashboard';

const ROLE_GREETING_TOKENS = new Set([
  'patient',
  'patients',
  'user',
  'users',
  'there',
  'friend',
  'hello',
  'hi',
  'guest',
  'anonymous',
  'caregiver',
  'family',
  'member',
  'customer',
  'subscriber',
]);

/** First name for a greeting, or null when the value is a role label or empty. */
export function personalGreetingName(name) {
  const first = String(name || '').trim().split(/\s+/)[0] || '';
  const token = first.replace(/[^a-z]/gi, '').toLowerCase();
  if (!token || token.length < 2 || ROLE_GREETING_TOKENS.has(token)) return null;
  if (first.includes('@') || first.includes('://')) return null;
  return first;
}

/** "Hi Nick," or a neutral "Hello," — never "Hi Patients,". */
export function greetingLine(name) {
  const first = personalGreetingName(name);
  return first ? `Hi ${first},` : 'Hello,';
}

/** Internal path only. Blocks protocol-relative and absolute URLs. */
export function safeInternalPath(value, fallback = PATIENT_DASHBOARD_PATH) {
  const raw = String(value || '').trim();
  if (!raw.startsWith('/') || raw.startsWith('//') || raw.includes('://') || raw.includes('\\')) {
    return fallback;
  }
  if (raw.includes('\n') || raw.includes('\r')) return fallback;
  return raw;
}

export function emailConfirmRedirectUrl(origin = 'https://curegate.ai') {
  const base = String(origin || 'https://curegate.ai').replace(/\/$/, '');
  return `${base}/auth/callback`;
}

export function hopefirstContinuePath({ caseReference = '', email = '' } = {}) {
  const params = new URLSearchParams();
  params.set('next', PATIENT_DASHBOARD_PATH);
  const ref = String(caseReference || '').trim();
  const addr = String(email || '').trim();
  if (ref) params.set('case_ref', ref);
  if (addr) params.set('email', addr);
  return `/auth/continue?${params.toString()}`;
}

/**
 * Where "Open HopeFirst" and the auth callback should send the browser.
 * Signed-in patients go to the dashboard (or a safe next path).
 * Signed-out visitors go to sign-in with that path preserved.
 */
export function continueDestination({
  signedIn = false,
  role = 'patient',
  next = '',
  caseReference = '',
  email = '',
} = {}) {
  const wanted = safeInternalPath(next, PATIENT_DASHBOARD_PATH);
  if (signedIn) {
    if (String(role || '').toLowerCase() === 'patient') return wanted;
    return dashboardPathForRole(role);
  }
  const params = new URLSearchParams();
  params.set('next', wanted);
  const ref = String(caseReference || '').trim();
  const addr = String(email || '').trim();
  if (ref) params.set('case_ref', ref);
  if (addr) params.set('email', addr);
  return `/patient-login?${params.toString()}`;
}

export function readAuthCallbackParams(search = '') {
  const params = new URLSearchParams(String(search || '').replace(/^\?/, ''));
  return {
    code: params.get('code') || '',
    tokenHash: params.get('token_hash') || '',
    type: params.get('type') || '',
    token: params.get('token') || '',
    next: params.get('next') || '',
    caseReference: params.get('case_ref') || '',
    email: params.get('email') || '',
    error: params.get('error') || '',
    errorDescription: params.get('error_description') || '',
  };
}

export function isSupabaseAuthReturn(search = '') {
  const parsed = readAuthCallbackParams(search);
  return Boolean(
    parsed.code
    || parsed.tokenHash
    || parsed.error
    || parsed.type === 'signup'
    || parsed.type === 'magiclink'
    || parsed.type === 'email',
  );
}
